import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useQuery } from "../../../hooks/useQuery";
import {
  makeUrl,
  navigate,
  Route,
  routes,
  useCurrentUrl,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { Text } from "../../Text";
import {
  HoverActions,
  NeutralIconBadge,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  RowNameLine,
  ScrollArea,
} from "../shared";
import { AuditLogDestinationModal } from "./AuditLogDestinationModal";
import { DestinationStatusPill } from "./DestinationStatusPill";

export function AuditLogsDestinations() {
  const z = useZero();
  const [destinations, destinationsResult] = useQuery(
    queries.auditLogDestinations()
  );
  const { destinationId: urlDestinationId } = useCurrentUrl(
    routes.settings.auditLogs.destinations
  );
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const items = useMemo(
    () =>
      destinations.map((d) => ({
        key: d.id,
        link: makeUrl(null, { destinationId: d.id }),
        name: (
          <RowNameLayout>
            <NeutralIconBadge iconName="output" />
            <RowNameContent>
              <RowNameLine>
                <Text
                  ellipsis
                  variant="text-regular-md"
                  color="var(--text-primary)"
                >
                  {d.name}
                </Text>
              </RowNameLine>
              <Text ellipsis variant="text-small" color="var(--text-secondary)">
                {d.url}
              </Text>
            </RowNameContent>
          </RowNameLayout>
        ),
        method: <PillBadge>{d.method}</PillBadge>,
        auth: d.appUsage?.connectedApp ? (
          <PillBadge icon="layers">{d.appUsage.connectedApp.name}</PillBadge>
        ) : null,
        status: <DestinationStatusPill destination={d} />,
        actions: (hasMouse: boolean) => (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="right"
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Delete destination",
                      onClick: () => {
                        setConfirmDelete({ id: d.id, name: d.name });
                      },
                    },
                  ],
                },
              ]}
            />
          </HoverActions>
        ),
      })),
    [destinations]
  );

  const isEmpty = destinations.length === 0;

  return (
    <ScrollArea>
      {isEmpty ? (
        destinationsResult.type === "complete" ? (
          <EmptyState
            iconName="output"
            title="No destinations"
            description="Add a destination to send audit logs to an external system."
          />
        ) : null
      ) : (
        <ListTable
          headers={{
            name: "Name",
            method: "Method",
            auth: "Auth",
            status: "Status",
            actions: "",
          }}
          columns={{ method: "sm", auth: "md", status: "md" }}
          items={items}
        />
      )}
      {urlDestinationId != null && (
        <AuditLogDestinationModal
          open
          destinationId={urlDestinationId}
          onClose={() =>
            navigate(null, { destinationId: "" }, { replace: true })
          }
        />
      )}
      <Route path={routes.settings.auditLogs.destinations.new}>
        {() => (
          <AuditLogDestinationModal
            open
            destinationId={null}
            onClose={() =>
              navigate(
                routes.settings.auditLogs.destinations,
                { destinationId: "" },
                { replace: true }
              )
            }
          />
        )}
      </Route>
      <ConfirmModal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) {
            z.mutate(
              sharedMutators.deleteAuditLogDestination({ id: confirmDelete.id })
            );
          }
          setConfirmDelete(null);
        }}
        title="Delete destination"
        description={
          confirmDelete
            ? `"${confirmDelete.name}" will stop receiving audit logs. Existing data at the destination is not affected.`
            : ""
        }
        confirmLabel="Delete destination"
        destructive
      />
    </ScrollArea>
  );
}
